/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import type { Agent } from '@shared/schema/agents';
import type { SystemAgentMetadata } from '@shared/schema/systemAgents';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Play,
  MoreHorizontal,
  Shield,
  BarChart3,
  GitBranch,
  FileText,
  Bot,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Calendar,
  Loader2,
} from 'lucide-react';
import { formatRelativeTime, getAgentCategoryLabel } from '../../lib/api/system-agents';
import { cronToHumanReadable } from './ScheduleSelector';

interface AgentCardProps {
  agent: Agent;
  lastRunAt?: string;
  lastRunResult?: 'pass' | 'warning' | 'fail';
  findingsCount?: number;
  schedule?: string;
  isRunning?: boolean;
  onClick: () => void;
  onRunNow: () => void;
}

export function AgentCard({
  agent,
  lastRunAt,
  lastRunResult,
  findingsCount = 0,
  schedule,
  isRunning = false,
  onClick,
  onRunNow,
}: AgentCardProps) {
  // Get icon based on agent category
  const getIcon = () => {
    switch (agent.category) {
      case 'system_audit':
        return <Shield className="h-8 w-8 text-red-500" />;
      case 'system_measurement':
        return <BarChart3 className="h-8 w-8 text-blue-500" />;
      case 'system_review':
        return <GitBranch className="h-8 w-8 text-purple-500" />;
      case 'system_evaluation':
        return <FileText className="h-8 w-8 text-green-500" />;
      default:
        return <Bot className="h-8 w-8 text-gray-500" />;
    }
  };

  // Get result indicator
  const getResultIndicator = () => {
    if (isRunning) {
      return (
        <div className="flex items-center gap-1.5 text-blue-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">Running...</span>
        </div>
      );
    }

    if (!lastRunResult) {
      return (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span className="text-sm">Never run</span>
        </div>
      );
    }

    switch (lastRunResult) {
      case 'pass':
        return (
          <div className="flex items-center gap-1.5 text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-sm font-medium">{findingsCount} findings</span>
          </div>
        );
      case 'warning':
        return (
          <div className="flex items-center gap-1.5 text-yellow-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">{findingsCount} warnings</span>
          </div>
        );
      case 'fail':
        return (
          <div className="flex items-center gap-1.5 text-red-600">
            <XCircle className="h-4 w-4" />
            <span className="text-sm font-medium">{findingsCount} critical</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Parse schedule for display
  const getScheduleDisplay = () => {
    if (!schedule) return 'No schedule';
    return cronToHumanReadable(schedule);
  };

  const metadata = agent.metadata as SystemAgentMetadata | undefined;

  return (
    <Card 
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              {getIcon()}
            </div>
            <div>
              <h3 className="font-semibold text-base leading-tight">
                {agent.displayName || agent.name}
              </h3>
              <Badge variant="secondary" className="mt-1 text-xs">
                {getAgentCategoryLabel(agent.category)}
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              // TODO: Show dropdown menu
            }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {agent.description}
        </p>

        {/* Status Row */}
        <div className="flex items-center justify-between text-sm mb-4">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              {lastRunAt ? `Last: ${formatRelativeTime(lastRunAt)}` : 'Never run'}
            </span>
          </div>
          {getResultIndicator()}
        </div>

        {/* Schedule */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
          <Calendar className="h-4 w-4" />
          <span>Schedule: {getScheduleDisplay()}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRunNow();
            }}
            disabled={isRunning || agent.status === 'inactive'}
            className="gap-1.5"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run Now
              </>
            )}
          </Button>
          {agent.status === 'inactive' && (
            <Badge variant="outline" className="text-muted-foreground">
              Disabled
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
